"use client";

import Image from "next/image";
import { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  FileCheck2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const SWATCHES: { name: string; token: string; hex: string }[] = [
  { name: "Navy deep", token: "--nayx-navy-950", hex: "#031B4B" },
  { name: "Navy", token: "--nayx-navy-800", hex: "#022F6B" },
  { name: "Navy mid", token: "--nayx-navy-700", hex: "#023D83" },
  { name: "Teal", token: "--nayx-teal-500", hex: "#038E98" },
  { name: "Teal bright", token: "--nayx-teal-400", hex: "#00A4A6" },
  { name: "Teal glow", token: "--nayx-teal-300", hex: "#35C2C2" },
];

const STATUS = [
  { label: "Pending", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  {
    label: "AI Review",
    cls: "bg-[#e6f6f6] text-[#027090] border-[#a9e0e0]",
  },
  {
    label: "Verified",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  { label: "Flagged", cls: "bg-amber-50 text-amber-700 border-amber-200" },
];

export default function Home() {
  const [count, setCount] = useState(0);
  const hydrated = count > 0;

  return (
    <main className="min-h-dvh bg-slate-50 text-slate-900">
      {/* Gradient hero — the signature navy→teal identity */}
      <section className="nayx-gradient relative overflow-hidden text-white">
        <div className="absolute -right-24 -top-24 size-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 size-80 rounded-full bg-[#00a4a6]/20 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <div className="mb-8 flex w-fit rounded-2xl bg-white/95 px-6 py-4 shadow-lg ring-1 ring-white/40 backdrop-blur">
            <Image
              src="/brand/nayx-logo.png"
              alt="Nayx — Intelligence Behind Every Shift"
              width={2746}
              height={1167}
              priority
              className="h-12 w-auto sm:h-14"
            />
          </div>

          <Badge className="mb-4 h-auto gap-1 px-3 py-2 text-sm border-white/25 bg-white/10 text-white hover:bg-white/15">
            <Sparkles className="mr-1 size-3" /> Brand system proof
          </Badge>

          <h1 className="max-w-2xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            Intelligence Behind Every Shift
          </h1>
          <p className="mt-4 max-w-xl text-lg text-white/80">
            AI healthcare staffing. This page proves the Nayx brand palette,
            typography, and component system are wired end-to-end.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              className="bg-white text-[#022f6b] hover:bg-white/90"
            >
              Primary action <ArrowRight className="ml-1 size-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Secondary
            </Button>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl space-y-8 px-6 py-12">
        {/* Palette */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#022f6b]">Brand palette</CardTitle>
            <CardDescription>
              Sampled from the Nayx logo · defined in{" "}
              <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                src/app/brand.css
              </code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6">
              {SWATCHES.map((s) => (
                <div key={s.token} className="space-y-2">
                  <div
                    className="h-16 rounded-lg border border-black/5 shadow-sm"
                    style={{ backgroundColor: s.hex }}
                  />
                  <div>
                    <p className="text-sm font-medium">{s.name}</p>
                    <p className="font-mono text-xs text-slate-500">{s.hex}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Component proofs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#022f6b]">
                <ShieldCheck className="size-5 text-[#00a4a6]" />
                Components on-brand
              </CardTitle>
              <CardDescription>
                shadcn/ui themed to Nayx primary + teal ring.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-wrap gap-2">
                <Button>Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="lic">License number (teal focus ring)</Label>
                <Input id="lic" placeholder="e.g. RN-1234567" />
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">
                  Verification states
                </p>
                <div className="flex flex-wrap gap-2">
                  {STATUS.map((s) => (
                    <span
                      key={s.label}
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.cls}`}
                    >
                      {s.label}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Gradient text + hydration proof */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#022f6b]">
                <FileCheck2 className="size-5 text-[#00a4a6]" />
                Live stack proof
              </CardTitle>
              <CardDescription>
                Next.js 16 · React 19 · Tailwind v4 · nginx + TLS
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-3xl font-semibold tracking-tight">
                <span className="nayx-gradient-text">Nayx</span>{" "}
                <span className="text-slate-400">gradient text</span>
              </p>

              <ul className="space-y-2">
                {[
                  "Geist font loaded",
                  "Navy→teal gradient renders",
                  "Brand tokens in brand.css",
                  "Served over HTTPS via nginx",
                ].map((c) => (
                  <li
                    key={c}
                    className="flex items-center gap-2 text-sm text-slate-700"
                  >
                    <CheckCircle2 className="size-4 text-emerald-600" />
                    {c}
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between rounded-lg bg-slate-100 p-3">
                <span className="text-sm text-slate-600">
                  {hydrated
                    ? `Interactive — clicked ${count}×`
                    : "Click to prove hydration"}
                </span>
                <Button size="sm" onClick={() => setCount((n) => n + 1)}>
                  Ping
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </main>
  );
}
